import { useState, useEffect, useCallback, useMemo, FC } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@features/ui';
import {
  getCouponCodes,
  createCouponCode,
  deleteCouponCode,
} from '@src/services/coupons';
import { TCouponCode } from '@src/types';

import { CodeRow } from './components/CodeRow';
import {
  CreateCodeForm,
  TCreateCodeFormPayload,
} from './components/CreateCodeForm';
import { EditCodeForm } from './components/EditCodeForm';

const PAGE_SIZE = 20;

interface CouponCodesModalProps {
  open: boolean;
  batchId: number | null;
  onOpenChange: (open: boolean) => void;
}

export const CouponCodesModal: FC<CouponCodesModalProps> = ({
  open,
  batchId,
  onOpenChange,
}) => {
  const [codes, setCodes] = useState<TCouponCode[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TCouponCode | null>(null);

  const fetchCodes = useCallback(
    (p = 1) => {
      if (!batchId) return;
      setLoading(true);
      getCouponCodes(batchId, { page: p, size: PAGE_SIZE })
        .then((res) => {
          const data = res.data;

          setCodes(data.codes ?? []);
          setTotal(data.total ?? 0);
          setHasNext(data.has_next ?? false);
          setPage(p);
        })
        .catch(() => toast.error('Failed to load coupon codes'))
        .finally(() => setLoading(false));
    },
    [batchId]
  );

  useEffect(() => {
    if (open && batchId) {
      fetchCodes(1);
      setSearch('');
    } else {
      setCodes([]);
      setTotal(0);
      setPage(1);
      setSearch('');
    }
  }, [open, batchId, fetchCodes]);

  // Client-side search filter on current page
  const filteredCodes = useMemo(() => {
    if (!search.trim()) return codes;
    const q = search.trim().toLowerCase();

    return codes.filter((c) => c.code.toLowerCase().includes(q));
  }, [codes, search]);

  const handleCreate = async (payload: TCreateCodeFormPayload) => {
    if (!batchId) return;
    await createCouponCode(batchId, {
      count: payload.count,
      prefix: payload.prefix || undefined,
      assigned_user_ids:
        payload.assigned_user_ids.length > 0
          ? payload.assigned_user_ids
          : undefined,
    });
    toast.success('Coupon code(s) created');
    fetchCodes(1);
  };

  const handleDelete = async (code: TCouponCode) => {
    if (!batchId) return;
    const ok = window.confirm(
      `Delete coupon code "${code.code}"? This action cannot be undone.`
    );

    if (!ok) return;
    try {
      await deleteCouponCode(batchId, code.id);
      toast.success('Coupon code deleted (mock)');
      fetchCodes(page);
    } catch {
      toast.error('Failed to delete coupon code');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
          size="unset"
        >
          <DialogHeader>
            <DialogTitle>
              Coupon Codes{batchId ? ` — Batch #${batchId}` : ''}
            </DialogTitle>
          </DialogHeader>

          {/* Search + toolbar */}
          <div className="flex items-center gap-3 py-2">
            <input
              className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              placeholder="Search by coupon code..."
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="text-sm text-gray-400 whitespace-nowrap">
              {filteredCodes.length}/{total}
            </span>
            <Button
              size="sm"
              variant="lime"
              onClick={() => setCreateOpen(true)}
            >
              + Create coupon
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-auto flex-1">
            {loading ? (
              <div className="py-10 text-center text-sm text-gray-500">
                Loading...
              </div>
            ) : filteredCodes.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">
                {search
                  ? 'No codes match your search.'
                  : 'No coupon codes found.'}
              </div>
            ) : (
              <Table className="w-full text-sm">
                <TableHeader className="bg-gray-500">
                  <TableRow className="border-b border-gray-200 text-left text-xs">
                    <TableHead className="py-2.5 px-3 font-medium">
                      Code
                    </TableHead>
                    <TableHead className="py-2.5 px-3 font-medium">
                      Used for
                    </TableHead>
                    <TableHead className="py-2.5 px-3 font-medium">
                      User
                    </TableHead>
                    <TableHead className="py-2.5 px-3 font-medium">
                      Affiliate
                    </TableHead>
                    <TableHead className="py-2.5 px-3 font-medium w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((code) => (
                    <CodeRow
                      key={code.id}
                      code={code}
                      onDelete={handleDelete}
                      onEdit={setEditTarget}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {(page > 1 || hasNext) && (
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <Button
                disabled={page <= 1 || loading}
                size="sm"
                variant="secondary"
                onClick={() => fetchCodes(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-500">Page {page}</span>
              <Button
                disabled={!hasNext || loading}
                size="sm"
                variant="secondary"
                onClick={() => fetchCodes(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sub-modals */}
      <CreateCodeForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />
      <EditCodeForm
        code={editTarget}
        open={editTarget !== null}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
      />
    </>
  );
};

export default CouponCodesModal;
